import React, { useRef, useMemo } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation';
import type { ForYouConfig } from '@atlassian/global-side-navigation/types';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import {
	SidebarNav4ContextProvider,
	useSidebarNav4,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useShouldRenderOverviews } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-overviews/src/controllers/index.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import {
	USER_NAVIGATION_CONFIGURATION_DEFAULT,
	USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx';
import { navPlatformSidebarResource } from '@atlassian/jira-nav-platform-integration-resources/src/services/sidebar/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { RECENT_QUERY_LIMIT } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';
import {
	FAVOURITE_QUERY_LIMIT,
	VALID_FAVORITE_TYPES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/common/constants.tsx';
import { useFilterMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-starred/src/common/controllers/use-filter-menu-items/index.tsx';

import { getForYouConfig } from '../common/ui/for-you/getForYouConfig.tsx';
import { useShortcuts } from '../controllers/use-shortcuts/index.tsx';
import { useItemsConfig } from '../controllers/use-items-config/index.tsx';
import type { GSNForContentSidebarQuery } from './__generated__/GSNForContentSidebarQuery.graphql';

export function GSNForContentSidebar(): React.JSX.Element | null {
	/*
		TODO: Add support for entryPoint routes

	*/
	// This is the non-EP solution for loading the data
	const { queryReference } = useRelayResource<GSNForContentSidebarQuery>(
		navPlatformSidebarResource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<GSNForContentSidebarQuery> | null>(null);

	// We don't want to use nullable query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		queryReferenceRef.current = queryReference;
	} else if (!queryReferenceRef.current) {
		return null;
	}

	return (
		<SidebarNav4ContextProvider>
			<GSNForContentSidebarInner queryReference={queryReferenceRef.current} />
		</SidebarNav4ContextProvider>
	);
}

type Props = {
	isShortcutsStatic?: boolean;
	queryReference: PreloadedQuery<GSNForContentSidebarQuery>;
};

// Exported for Storybook, but it also fixes https://hello.atlassian.net/wiki/spaces/~70121adfd59d619854e929bc192ed5ecb9b51/pages/3616160535
// By passing the queryReference as a param, it makes it easier to use for StoryBook.
export function GSNForContentSidebarInner(props: Props): React.JSX.Element {
	const { isShortcutsStatic = false, queryReference } = props;
	const queryRef = usePreloadedQuery<GSNForContentSidebarQuery>(
		// Need to disable this rule for the useNetworkSideNavUserData fragment (which is inside GSN)
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		graphql`
			query GSNForContentSidebarQuery(
				$cloudId: ID!
				$entityAriProjects: ID!
				$entityAriPlans: ID!
				$entityAriUserSideNavSettings: ID!
				$isAppsExpanded: Boolean = false
				$isFiltersExpanded: Boolean = false
				$isPlansExpanded: Boolean = false
				$isProjectsExpanded: Boolean = false
				$isOperationsExpanded: Boolean = false
				$isOverviewsExpanded: Boolean = false
				$navPlatMenuDataEntityAri: ID!
				$navPlatSidebarDataEntityAri: ID!
				$ownerAri: ID
			) {
				...useNetworkSideNavUserData
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				...Nav4Apps
				...Nav4Filters
				...Nav4Operations
				...Nav4Overviews
				...Nav4Plans
				...Nav4Projects
			}
		`,
		queryReference,
	);

	const shouldRenderOverviews = useShouldRenderOverviews();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const ownerId = useAccountId();
	const { isSelectedPath } = useSidebarNav4();

	// Note: When Jira GSN adoption rolls out, it should rollout with "network-migration" for 3 months
	//  to give time for all old user settings to be stored in the new records.
	//
	//    - customizationStore="network-migration"
	//
	//  After 3 months of being rolled-out, create a new FG to:
	//   - Change customizationStore to "network"
	//   - @include(if: $fgVariable) for useNetworkSideNavUserData.
	//   - After that has rolled out,
	//
	const customizationStore = 'network-migration';

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		expVal('navx-1982-change-order-of-teams-and-goals', 'isEnabled', false)
			? USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED.navItems
			: USER_NAVIGATION_CONFIGURATION_DEFAULT.navItems,
	);

	const items = useItemsConfig(navItems, queryRef, shouldRenderOverviews);
	const recentEntryPointParams = useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]);
	const starredEntryPointParams = useMemo(
		() => ({
			cloudId,
			filter: { types: VALID_FAVORITE_TYPES, sort: { order: 'DESC' as const } },
			count: FAVOURITE_QUERY_LIMIT,
		}),
		[cloudId],
	);

	const { formatMessage } = useIntl();
	const starredFilterMenuItemDataRaw = useFilterMenuItems();
	const starredFilterConfig = useMemo(
		() =>
			starredFilterMenuItemDataRaw.map((item) => ({
				key: item.filterType,
				label: formatMessage(item.message),
				elemBefore: <item.Icon />,
			})),
		[formatMessage, starredFilterMenuItemDataRaw],
	);

	const forYouConfig = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			getForYouConfig({
				recentEntryPointParams,
				starredEntryPointParams,
				starredFilterConfig,
			}) as ForYouConfig<ObjectListFlyoutItem>,
		[recentEntryPointParams, starredEntryPointParams, starredFilterConfig],
	);

	// Determine peekMenuId from selectedPath
	const peekMenuId = navItems.find((item) => isSelectedPath(item.menuId))?.menuId;

	const shortcuts = useShortcuts();

	const networkStorageParams = useMemo(() => {
		const siteId = cloudId;
		const entityAri = JiraWorkspaceAri.create({ siteId, activationId }).toString();
		const ownerAri = (ownerId && IdentityUserAri.create({ userId: ownerId }).toString()) || '';

		return { activationId, entityAri, ownerAri, siteId, queryRef };
	}, [cloudId, activationId, ownerId, queryRef]);

	return (
		<GlobalSideNavigation
			app="jira"
			appLabel="Jira"
			customizationStore={customizationStore}
			forYou={forYouConfig}
			items={items}
			networkStorageParams={networkStorageParams}
			peekMenuId={peekMenuId}
			shortcuts={shortcuts}
			isShortcutsStatic={isShortcutsStatic} // Needed for VR tests as the URL can't be mocked
			testId="nav-platform-integration-sidebar.ui.global-side-navigation"
		/>
	);
}
